import React, { useState, useRef, createElement } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { XIcon, MailIcon, SmartphoneIcon, LinkIcon, CheckIcon, UserIcon, EyeOffIcon, SendIcon, DollarSignIcon, QrCodeIcon, DownloadIcon, CalendarIcon, PercentIcon, BuildingIcon, ArrowRightIcon, ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { Recipient, Supplier, Installment } from './InstallmentPayment';
import { QRCodeSVG } from 'qrcode.react';
interface ShareLinkModalProps {
  onClose: () => void;
  splitType?: 'dates' | 'recipients' | 'commission' | null;
  recipients?: Recipient[];
  installments?: Installment[];
  suppliers?: Supplier[];
  totalAmount?: string;
  commissionType?: 'percentage' | 'fixed';
}
export function ShareLinkModal({
  onClose,
  splitType = null,
  recipients = [],
  installments = [],
  suppliers = [],
  totalAmount = '0',
  commissionType = 'percentage'
}: ShareLinkModalProps) {
  const {
    t
  } = useTranslation('payments');
  const [step, setStep] = useState<'preview' | 'share'>(() => {
    // Recipients mode goes straight to share (multi-recipient view)
    // No splitType (standard) also goes straight to share
    if (splitType === 'recipients' || !splitType) return 'share';
    // Schedule and commission start with preview
    return 'preview';
  });
  const [selectedOption, setSelectedOption] = useState<'email' | 'sms' | 'copy' | 'qrcode' | null>(null);
  const [recipientInput, setRecipientInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [qrDownloaded, setQrDownloaded] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement>(null);
  // For multiple recipients
  const [recipientContactMethods, setRecipientContactMethods] = useState<Record<number, 'email' | 'sms' | 'both'>>({});
  const [copiedRecipientId, setCopiedRecipientId] = useState<number | null>(null);
  const [recipientsViewTotalSettings, setRecipientsViewTotalSettings] = useState<Record<number, boolean>>(() => {
    const initialSettings: Record<number, boolean> = {};
    recipients.forEach(r => {
      initialSettings[r.id] = false;
    });
    return initialSettings;
  });
  const hasMultipleRecipients = splitType === 'recipients' && recipients.length > 0;
  const handleCopyLink = (recipientId?: number) => {
    const linkToCopy = recipientId ? `https://payment.example.com/pay/${recipientId}-abc123${recipientsViewTotalSettings[recipientId] ? '?showTotal=true' : ''}` : 'https://payment.example.com/pay/abc123';
    navigator.clipboard.writeText(linkToCopy);
    if (recipientId) {
      setCopiedRecipientId(recipientId);
      setTimeout(() => setCopiedRecipientId(null), 2000);
    } else {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  const handleDownloadQR = () => {
    if (!qrCodeRef.current) return;
    const canvas = qrCodeRef.current.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'payment-qrcode.png';
    link.href = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setQrDownloaded(true);
    setTimeout(() => setQrDownloaded(false), 2000);
  };
  const handleSend = () => {
    onClose();
  };
  const getContactMethod = (recipient: Recipient): 'email' | 'sms' | 'both' => {
    if (recipientContactMethods[recipient.id]) {
      return recipientContactMethods[recipient.id];
    }
    if (recipient.email && recipient.phone) return 'both';
    if (recipient.email) return 'email';
    if (recipient.phone) return 'sms';
    return 'email';
  };
  const setContactMethod = (recipientId: number, method: 'email' | 'sms' | 'both') => {
    setRecipientContactMethods(prev => ({
      ...prev,
      [recipientId]: method
    }));
  };
  const toggleRecipientTotalView = (recipientId: number) => {
    setRecipientsViewTotalSettings(prev => ({
      ...prev,
      [recipientId]: !prev[recipientId]
    }));
  };
  // Calculate commission values
  const totalAmountNum = parseFloat(totalAmount) || 0;
  const totalCommission = suppliers.reduce((sum, s) => {
    if (commissionType === 'percentage') {
      return sum + totalAmountNum * (parseFloat(s.percentage || '0') / 100);
    }
    return sum + (parseFloat(s.amount) || 0);
  }, 0);
  const businessShare = totalAmountNum - totalCommission;
  const getModalTitle = () => {
    if (step === 'preview') {
      if (splitType === 'dates') return 'Payment schedule preview';
      if (splitType === 'commission') return 'Commission split preview';
    }
    if (hasMultipleRecipients) return 'Share payment link';
    return 'Share payment link';
  };
  // ─── SCHEDULE PREVIEW ───
  const renderSchedulePreview = () => <div className="space-y-4" data-id="element-7612">
      <p className="text-sm text-gray-600" data-id="element-7613">
        {t('components.renderSchedulePreview.review_the_payment_schedule_before_creating')}
      </p>

      <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-7614">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-7615">
          <div className="grid grid-cols-12 gap-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7616">
            <div className="col-span-1" data-id="element-7617">#</div>
            <div className="col-span-3" data-id="element-7618">
              {t('components.BulkPaymentPreview.amount')}
            </div>
            <div className="col-span-3" data-id="element-7619">
              {t('components.InstallmentPayment.due_date')}
            </div>
            <div className="col-span-3" data-id="element-7620">
              {t('components.InstallmentPayment.send_link_on')}
            </div>
            <div className="col-span-2" data-id="element-7621">
              {t('components.BulkPaymentList.status')}
            </div>
          </div>
        </div>
        <div className="divide-y divide-gray-100" data-id="element-7622">
          {installments.map((inst, index) => <div key={inst.id} className="px-4 py-3.5" data-id="element-7623">
              <div className="grid grid-cols-12 gap-3 items-center" data-id="element-7624">
                <div className="col-span-1" data-id="element-7625">
                  <div className="w-6 h-6 flex items-center justify-center bg-[#FFC70A]/10 text-[#B28B07] rounded-full font-bold text-xs" data-id="element-7626">
                    {index + 1}
                  </div>
                </div>
                <div className="col-span-3" data-id="element-7627">
                  <span className="text-sm font-semibold text-gray-900" data-id="element-7628">
                    {formatCurrencyAmount(parseFloat(inst.amount || '0'), {
                  fractionDigits: 2
                })}
                  </span>
                  {inst.description && <p className="text-xs text-gray-500 mt-0.5" data-id="element-7629">
                      {inst.description}
                    </p>}
                </div>
                <div className="col-span-3" data-id="element-7630">
                  <div className="flex items-center text-sm text-gray-700" data-id="element-7631">
                    <CalendarIcon size={13} className="mr-1.5 text-gray-400" data-id="element-7632" />
                    {inst.dueDate || 'Not set'}
                  </div>
                </div>
                <div className="col-span-3" data-id="element-7633">
                  <div className="flex items-center text-sm text-gray-700" data-id="element-7634">
                    <SendIcon size={13} className="mr-1.5 text-gray-400" data-id="element-7635" />
                    {inst.sendLinkDate || 'Auto'}
                  </div>
                </div>
                <div className="col-span-2" data-id="element-7636">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-7637">
                    {t('components.renderSchedulePreview.pending')}
                  </span>
                </div>
              </div>
            </div>)}
        </div>
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200" data-id="element-7638">
          <div className="flex justify-between items-center" data-id="element-7639">
            <span className="text-sm font-medium text-gray-600" data-id="element-7640">
              {t('components.renderSchedulePreview.total')}
            </span>
            <span className="text-sm font-bold text-gray-900" data-id="element-7641">
              {formatCurrencyAmount(installments.reduce((sum, i) => sum + parseFloat(i.amount || '0'), 0), {
              fractionDigits: 2
            })}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-[#FFC70A]/5 border border-[#FFC70A]/20 rounded-xl p-4" data-id="element-7642">
        <div className="flex items-start gap-3" data-id="element-7643">
          <CalendarIcon size={18} className="text-[#B28B07] mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-7644" />

          <div data-id="element-7645">
            <p className="text-sm font-medium text-gray-900" data-id="element-7646">
              {t('components.renderSchedulePreview.automatic_link_delivery')}
            </p>
            <p className="text-xs text-gray-600 mt-0.5" data-id="element-7647">
              {t('components.renderSchedulePreview.payment_links_will_be_sent_automatically')}
            </p>
          </div>
        </div>
      </div>
    </div>;
  // ─── COMMISSION PREVIEW ───
  const renderCommissionPreview = () => <div className="space-y-4" data-id="element-7648">
      <p className="text-sm text-gray-600" data-id="element-7649">
        {t('components.renderCommissionPreview.review_the_commission_breakdown_before_creating')}
      </p>

      {/* Total amount card */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-5" data-id="element-7650">
        <div className="flex items-center justify-between mb-4" data-id="element-7651">
          <span className="text-sm font-medium text-gray-500" data-id="element-7652">
            {t('components.InstallmentPayment.total_payment_amount')}
          </span>
          <span className="text-xl font-bold text-gray-900" data-id="element-7653">
            {formatCurrencyAmount(totalAmountNum, {
            fractionDigits: 2
          })}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-7654">
          <div className="h-full rounded-full bg-gradient-to-r from-[#FFC70A] to-[#FFC70A]/70" style={{
          width: totalAmountNum > 0 ? `${Math.min(totalCommission / totalAmountNum * 100, 100)}%` : '0%'
        }} data-id="element-7655" />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500" data-id="element-7656">
          <span className="flex items-center gap-1" data-id="element-7657">
            <span className="w-2 h-2 rounded-full bg-[#FFC70A]" data-id="element-7658" />
            {t('components.renderCommissionPreview.commission_usd')}{' '}
            {formatCurrencyAmount(totalCommission, {
            fractionDigits: 2
          })}
          </span>
          <span className="flex items-center gap-1" data-id="element-7659">
            <span className="w-2 h-2 rounded-full bg-gray-300" data-id="element-7660" />
            {t('components.renderCommissionPreview.your_share_usd')}{' '}
            {formatCurrencyAmount(businessShare, {
            fractionDigits: 2
          })}
          </span>
        </div>
      </div>

      {/* Supplier breakdown */}
      <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-7661">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-7662">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7663">
            {t('components.renderCommissionPreview.supplier_commission_breakdown')}
          </h4>
        </div>
        <div className="divide-y divide-gray-100" data-id="element-7664">
          {suppliers.map((supplier, index) => {
          const supplierAmount = commissionType === 'percentage' ? totalAmountNum * (parseFloat(supplier.percentage || '0') / 100) : parseFloat(supplier.amount) || 0;
          const supplierPercentage = commissionType === 'percentage' ? parseFloat(supplier.percentage || '0') : totalAmountNum > 0 ? supplierAmount / totalAmountNum * 100 : 0;
          return <div key={supplier.id} className="px-4 py-4" data-id="element-7665">
                <div className="flex items-center justify-between" data-id="element-7666">
                  <div className="flex items-center gap-3" data-id="element-7667">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#FFC70A]/10 text-[#B28B07] rounded-full font-bold text-xs flex-shrink-0" data-id="element-7668">
                      {index + 1}
                    </div>
                    <div data-id="element-7669">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-7670">
                        {supplier.name || `Supplier ${index + 1}`}
                      </p>
                      {supplier.email && <p className="text-xs text-gray-500" data-id="element-7671">
                          {supplier.email}
                        </p>}
                    </div>
                  </div>
                  <div className="text-right" data-id="element-7672">
                    <p className="text-sm font-bold text-gray-900" data-id="element-7673">
                      {formatCurrencyAmount(supplierAmount, {
                    fractionDigits: 2
                  })}
                    </p>
                    <p className="text-xs text-[#B28B07] font-medium" data-id="element-7674">
                      {supplierPercentage.toFixed(1)}
                      {t('components.renderCommissionPreview.of_total')}
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* Your business share */}
        <div className="px-4 py-4 bg-gray-50 border-t border-gray-200" data-id="element-7675">
          <div className="flex items-center justify-between" data-id="element-7676">
            <div className="flex items-center gap-3" data-id="element-7677">
              <div className="w-8 h-8 flex items-center justify-center bg-gray-200 text-gray-600 rounded-full flex-shrink-0" data-id="element-7678">
                <BuildingIcon size={14} data-id="element-7679" />
              </div>
              <div data-id="element-7680">
                <p className="text-sm font-semibold text-gray-900" data-id="element-7681">
                  {t('components.renderCommissionPreview.your_business')}
                </p>
                <p className="text-xs text-gray-500" data-id="element-7682">
                  {t('components.renderCommissionPreview.remaining_after_commission')}
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-7683">
              <p className="text-sm font-bold text-gray-900" data-id="element-7684">
                {formatCurrencyAmount(businessShare, {
                fractionDigits: 2
              })}
              </p>
              <p className="text-xs text-gray-500 font-medium" data-id="element-7685">
                {totalAmountNum > 0 ? (businessShare / totalAmountNum * 100).toFixed(1) : '0.0'}
                {t('components.renderCommissionPreview.of_total')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
  // ─── MULTI-RECIPIENT SHARE VIEW ───
  const renderMultiRecipientShare = () => <>
      <div className="flex justify-between items-center mb-4" data-id="element-7686">
        <p className="text-gray-600 font-medium" data-id="element-7687">
          {t('components.renderMultiRecipientShare.send_unique_payment_links_to_each')}
        </p>
      </div>
      <div className="space-y-4 max-h-[50vh] overflow-y-auto" data-id="element-7688">
        {recipients.map((recipient, index) => <div key={recipient.id} className="border border-gray-200 rounded-xl p-4" data-id="element-7689">
            <div className="flex items-center mb-3" data-id="element-7690">
              <div className="w-10 h-10 rounded-full bg-[#FFC70A]/10 flex items-center justify-center flex-shrink-0" data-id="element-7691">
                <UserIcon size={20} className="text-[#B28B07]" aria-hidden="true" data-id="element-7692" />
              </div>
              <div className="ml-3" data-id="element-7693">
                <h4 className="font-medium text-gray-800" data-id="element-7694">
                  {t('components.renderMultiRecipientShare.recipient')}{' '}
                  {index + 1}
                </h4>
                <p className="text-sm text-gray-500" data-id="element-7695">
                  {recipient.email || recipient.phone}
                </p>
              </div>
            </div>

            {/* Toggle */}
            <div className="mb-3" data-id="element-7696">
              <label className="flex items-center cursor-pointer" data-id="element-7697">
                <input type="checkbox" className="sr-only" checked={recipientsViewTotalSettings[recipient.id] || false} onChange={() => toggleRecipientTotalView(recipient.id)} data-id="element-7698" />

                <div className={`w-10 h-5 rounded-full transition-colors ${recipientsViewTotalSettings[recipient.id] ? 'bg-[#FFC70A]' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-7699">
                  <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${recipientsViewTotalSettings[recipient.id] ? 'translate-x-5' : ''}`} data-id="element-7700" />
                </div>
                <span className="ml-2 text-sm text-gray-600" data-id="element-7701">
                  {recipientsViewTotalSettings[recipient.id] ? <span className="flex items-center" data-id="element-7702">
                      <DollarSignIcon size={14} className="mr-1" data-id="element-7703" />
                      {t('components.renderMultiRecipientShare.allow_recipient_to_view_the_total')}
                    </span> : <span className="flex items-center" data-id="element-7704">
                      <EyeOffIcon size={14} className="mr-1" data-id="element-7705" />
                      {t('components.renderMultiRecipientShare.recipient_will_only_see_their_portion')}
                    </span>}
                </span>
              </label>
            </div>

            {/* Payment link */}
            <div className="mb-3" data-id="element-7706">
              <div className="flex" data-id="element-7707">
                <input type="text" className="flex-1 px-3 py-2 border border-gray-200 rounded-l-lg focus:ring-#[FFC70A]/20 focus:border-#[FFC70A] bg-gray-50 text-sm" value={`https://payment.example.com/pay/${recipient.id}-abc123${recipientsViewTotalSettings[recipient.id] ? '?showTotal=true' : ''}`} readOnly data-id="element-7708" />

                <button className="px-3 py-2 bg-[#FFC70A] text-white rounded-r-lg hover:bg-[#E5B309] transition-colors flex items-center text-sm font-medium" onClick={() => handleCopyLink(recipient.id)} data-id="element-7709">
                  {copiedRecipientId === recipient.id ? <>
                      <CheckIcon size={14} className="mr-1" data-id="element-7710" />
                      {t('components.renderMultiRecipientShare.copied')}
                    </> : <>
                      <LinkIcon size={14} className="mr-1" data-id="element-7711" />
                      {t('components.renderMultiRecipientShare.copy')}
                    </>}
                </button>
              </div>
            </div>

            {/* Contact options */}
            {(recipient.email || recipient.phone) && <div className="mt-3" data-id="element-7712">
                <h5 className="text-xs font-medium text-gray-500 mb-2" data-id="element-7713">
                  {t('components.renderMultiRecipientShare.send_payment_link_via')}
                </h5>
                <div className="flex space-x-2" data-id="element-7714">
                  {recipient.email && recipient.phone && <>
                      <button className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${getContactMethod(recipient) === 'email' ? 'bg-[#FFC70A]/10 text-[#FFC70A] border border-[#FFC70A]/30' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'email')} data-id="element-7715">
                        <MailIcon size={14} className="mr-1.5" data-id="element-7716" />
                        {t('components.RecurringPayment.email')}
                      </button>
                      <button className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${getContactMethod(recipient) === 'sms' ? 'bg-[#FFC70A]/10 text-[#FFC70A] border border-[#FFC70A]/30' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'sms')} data-id="element-7717">
                        <SmartphoneIcon size={14} className="mr-1.5" data-id="element-7718" />
                        {t('components.renderMultiRecipientShare.sms')}
                      </button>
                      <button className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${getContactMethod(recipient) === 'both' ? 'bg-[#FFC70A] text-white border border-[#FFC70A]' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'both')} data-id="element-7719">
                        {t('components.renderMultiRecipientShare.both')}
                      </button>
                    </>}
                  {recipient.email && !recipient.phone && <button className="flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium bg-[#FFC70A]/10 text-[#FFC70A] border border-[#FFC70A]/30 cursor-default" data-id="element-7720">
                      <MailIcon size={14} className="mr-1.5" data-id="element-7721" />
                      {t('components.renderMultiRecipientShare.email_only')}
                    </button>}
                  {!recipient.email && recipient.phone && <button className="flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium bg-[#FFC70A]/10 text-[#FFC70A] border border-[#FFC70A]/30 cursor-default" data-id="element-7722">
                      <SmartphoneIcon size={14} className="mr-1.5" data-id="element-7723" />
                      {t('components.renderMultiRecipientShare.sms_only')}
                    </button>}
                </div>
              </div>}

            {/* Display contact info */}
            <div className="mt-3 text-sm space-y-1" data-id="element-7724">
              {(getContactMethod(recipient) === 'email' || getContactMethod(recipient) === 'both') && recipient.email && <div className="flex items-center text-gray-600" data-id="element-7725">
                    <MailIcon size={14} className="mr-1.5" data-id="element-7726" />
                    {recipient.email}
                  </div>}
              {(getContactMethod(recipient) === 'sms' || getContactMethod(recipient) === 'both') && recipient.phone && <div className="flex items-center text-gray-600" data-id="element-7727">
                    <SmartphoneIcon size={14} className="mr-1.5" data-id="element-7728" />
                    {recipient.phone}
                  </div>}
            </div>
          </div>)}
      </div>
    </>;
  // ─── STANDARD SHARE VIEW (single link) ───
  const renderStandardShare = () => <>
      <p className="text-gray-600 mb-4" data-id="element-7729">
        {t('components.renderStandardShare.choose_how_you_want_to_share')}
      </p>
      <div className="space-y-3" data-id="element-7730">
        {/* Email option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'email' ? 'border-[#FFC70A] bg-[#FFC70A]/[0.03] ring-1 ring-[#FFC70A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('email')} data-id="element-7731">
          <div className="flex items-center" data-id="element-7732">
            <div className="w-10 h-10 rounded-full bg-[#FFC70A]/10 flex items-center justify-center flex-shrink-0" data-id="element-7733">
              <MailIcon size={20} className="text-[#B28B07]" data-id="element-7734" />
            </div>
            <div className="ml-4 flex-1" data-id="element-7735">
              <h4 className="font-medium text-gray-800" data-id="element-7736">
                {t('components.renderStandardShare.send_via_email')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-7737">
                {t('components.renderStandardShare.send_the_payment_link_to_their')}
              </p>
            </div>
          </div>
          {selectedOption === 'email' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-7738">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7739">
                {t('components.InstallmentPayment.attrs.email_address')}
              </label>
              <input type="email" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-#[FFC70A]/20 focus:border-#[FFC70A] transition-colors" placeholder={t('components.renderStandardShare.attrs.nameexamplecom')} value={recipientInput} onChange={e => setRecipientInput(e.target.value)} data-id="element-7740" />
            </div>}
        </div>

        {/* SMS option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'sms' ? 'border-[#FFC70A] bg-[#FFC70A]/[0.03] ring-1 ring-[#FFC70A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('sms')} data-id="element-7741">
          <div className="flex items-center" data-id="element-7742">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0" data-id="element-7743">
              <SmartphoneIcon size={20} className="text-green-600" data-id="element-7744" />
            </div>
            <div className="ml-4 flex-1" data-id="element-7745">
              <h4 className="font-medium text-gray-800" data-id="element-7746">
                {t('components.renderStandardShare.send_via_text_message')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-7747">
                {t('components.renderStandardShare.send_the_payment_link_to_their_2')}
              </p>
            </div>
          </div>
          {selectedOption === 'sms' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-7748">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7749">
                {t('components.InstallmentPayment.attrs.phone_number')}
              </label>
              <input type="tel" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-#[FFC70A]/20 focus:border-#[FFC70A] transition-colors" placeholder="+1 (555) 123-4567" value={recipientInput} onChange={e => setRecipientInput(e.target.value)} data-id="element-7750" />
            </div>}
        </div>

        {/* QR Code option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'qrcode' ? 'border-[#FFC70A] bg-[#FFC70A]/[0.03] ring-1 ring-[#FFC70A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('qrcode')} data-id="element-7751">
          <div className="flex items-center" data-id="element-7752">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-7753">
              <QrCodeIcon size={20} className="text-amber-600" data-id="element-7754" />
            </div>
            <div className="ml-4 flex-1" data-id="element-7755">
              <h4 className="font-medium text-gray-800" data-id="element-7756">
                {t('components.renderStandardShare.generate_qr_code')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-7757">
                {t('components.renderStandardShare.create_a_scannable_qr_code_for')}
              </p>
            </div>
          </div>
          {selectedOption === 'qrcode' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-7758">
              <div className="flex flex-col items-center" data-id="element-7759">
                <div ref={qrCodeRef} className="bg-white p-4 rounded-lg mb-3" data-id="element-7760">
                  <QRCodeSVG value="https://payment.example.com/pay/abc123" size={200} level="H" includeMargin={true} data-id="element-7761" />
                </div>
                <button className="px-4 py-2 bg-[#FFC70A] text-white rounded-lg hover:bg-[#E5B309] transition-colors flex items-center font-medium text-sm" onClick={handleDownloadQR} data-id="element-7762">
                  {qrDownloaded ? <>
                      <CheckIcon size={16} className="mr-2" data-id="element-7763" />
                      {t('components.renderStandardShare.downloaded')}
                    </> : <>
                      <DownloadIcon size={16} className="mr-2" data-id="element-7764" />
                      {t('components.renderStandardShare.download_qr_code')}
                    </>}
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center" data-id="element-7765">
                  {t('components.renderStandardShare.customers_can_scan_this_qr_code')}
                </p>
              </div>
            </div>}
        </div>

        {/* Copy link option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'copy' ? 'border-[#FFC70A] bg-[#FFC70A]/[0.03] ring-1 ring-[#FFC70A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('copy')} data-id="element-7766">
          <div className="flex items-center" data-id="element-7767">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-7768">
              <LinkIcon size={20} className="text-purple-600" data-id="element-7769" />
            </div>
            <div className="ml-4 flex-1" data-id="element-7770">
              <h4 className="font-medium text-gray-800" data-id="element-7771">
                {t('components.renderStandardShare.copy_link_to_share')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-7772">
                {t('components.renderStandardShare.copy_the_payment_link_to_share')}
              </p>
            </div>
          </div>
          {selectedOption === 'copy' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-7773">
              <div className="flex" data-id="element-7774">
                <input type="text" className="flex-1 px-3 py-2.5 border border-gray-200 rounded-l-lg focus:ring-#[FFC70A]/20 focus:border-#[FFC70A] bg-gray-50 text-sm" value="https://payment.example.com/pay/abc123" readOnly data-id="element-7775" />

                <button className="px-4 py-2.5 bg-[#FFC70A] text-white rounded-r-lg hover:bg-[#E5B309] transition-colors flex items-center font-medium text-sm" onClick={() => handleCopyLink()} data-id="element-7776">
                  {copied ? <>
                      <CheckIcon size={16} className="mr-1" data-id="element-7777" />
                      {t('components.renderMultiRecipientShare.copied')}
                    </> : <>
                      <LinkIcon size={16} className="mr-1" data-id="element-7778" />
                      {t('components.renderMultiRecipientShare.copy')}
                    </>}
                </button>
              </div>
            </div>}
        </div>
      </div>
    </>;
  // ─── FOOTER BUTTONS ───
  const renderFooter = () => {
    if (step === 'preview') {
      return <div className="p-4 border-t border-gray-200 flex justify-between" data-id="element-7779">
          <button className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium" onClick={onClose} data-id="element-7780">
            {t('components.CardOnFilePayment.cancel')}
          </button>
          <button className="px-5 py-2.5 bg-[#FFC70A] text-white rounded-lg hover:bg-[#E5B309] transition-colors flex items-center text-sm font-medium" onClick={() => setStep('share')} data-id="element-7781">
            {t('components.renderFooter.continue_to_share')}
            <ArrowRightIcon size={16} className="ml-2" data-id="element-7782" />
          </button>
        </div>;
    }
    // Share step
    const showBackButton = splitType === 'dates' || splitType === 'commission';
    return <div className="p-4 border-t border-gray-200 flex justify-between" data-id="element-7783">
        <div className="flex items-center gap-2" data-id="element-7784">
          {showBackButton && <button className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium flex items-center" onClick={() => setStep('preview')} data-id="element-7785">
              <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-7786" />
              {t('components.BulkPaymentPreview.back')}
            </button>}
          <button className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium" onClick={onClose} data-id="element-7787">
            {t('components.CardOnFilePayment.cancel')}
          </button>
        </div>
        <div data-id="element-7788">
          {hasMultipleRecipients ? <button className="px-5 py-2.5 bg-[#FFC70A] text-white rounded-lg hover:bg-[#E5B309] transition-colors flex items-center text-sm font-medium" onClick={handleSend} data-id="element-7789">
              <SendIcon size={16} className="mr-2" data-id="element-7790" />
              {t('components.renderFooter.send_to_all_recipients')}
            </button> : <>
              {(selectedOption === 'email' || selectedOption === 'sms') && <button className="px-5 py-2.5 bg-#[#FFC70A] text-white rounded-lg hover:bg-#[#E5B309] transition-colors flex items-center text-sm font-medium disabled:opacity-50" onClick={handleSend} disabled={!recipientInput} data-id="element-7791">
                  <SendIcon size={16} className="mr-2" data-id="element-7792" />
                  {t('components.renderFooter.send')}
                </button>}
              {(selectedOption === 'copy' || selectedOption === 'qrcode') && <button className="px-5 py-2.5 bg-[#FFC70A] text-white rounded-lg hover:bg-[#E5B309] transition-colors text-sm font-medium" onClick={onClose} data-id="element-7793">
                  {t('components.renderFooter.done')}
                </button>}
              {!selectedOption && <button className="px-5 py-2.5 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed" disabled data-id="element-7794">
                  {t('components.renderFooter.select_an_option')}
                </button>}
            </>}
        </div>
      </div>;
  };
  return createPortal(<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" data-id="element-7795">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 overflow-hidden" data-id="element-7796">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-7797">
          <div className="flex items-center gap-3" data-id="element-7798">
            {step === 'preview' && splitType === 'dates' && <div className="w-8 h-8 rounded-lg bg-[#FFC70A]/10 flex items-center justify-center" data-id="element-7799">
                <CalendarIcon size={16} className="text-[#B28B07]" data-id="element-7800" />
              </div>}
            {step === 'preview' && splitType === 'commission' && <div className="w-8 h-8 rounded-lg bg-[#FFC70A]/10 flex items-center justify-center" data-id="element-7801">
                <PercentIcon size={16} className="text-[#B28B07]" data-id="element-7802" />
              </div>}
            <div data-id="element-7803">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-7804">
                {getModalTitle()}
              </h3>
              {step === 'preview' && <div className="flex items-center gap-2 mt-1" data-id="element-7805">
                  <div className="flex items-center gap-1" data-id="element-7806">
                    <div className="w-5 h-5 rounded-full bg-[#FFC70A] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-7807">
                      1
                    </div>
                    <span className="text-xs font-medium text-[#B28B07]" data-id="element-7808">
                      {t('components.ShareLinkModal.review')}
                    </span>
                  </div>
                  <div className="w-4 h-px bg-gray-300" data-id="element-7809" />
                  <div className="flex items-center gap-1" data-id="element-7810">
                    <div className="w-5 h-5 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-[10px] font-bold" data-id="element-7811">
                      2
                    </div>
                    <span className="text-xs text-gray-400" data-id="element-7812">
                      {t('components.ShareLinkModal.share')}
                    </span>
                  </div>
                </div>}
              {step === 'share' && (splitType === 'dates' || splitType === 'commission') && <div className="flex items-center gap-2 mt-1" data-id="element-7813">
                    <div className="flex items-center gap-1" data-id="element-7814">
                      <div className="w-5 h-5 rounded-full bg-[#FFC70A] text-white flex items-center justify-center" data-id="element-7815">
                        <CheckIcon size={10} data-id="element-7816" />
                      </div>
                      <span className="text-xs text-gray-400" data-id="element-7817">
                        {t('components.ShareLinkModal.review')}
                      </span>
                    </div>
                    <div className="w-4 h-px bg-[#FFC70A]" data-id="element-7818" />
                    <div className="flex items-center gap-1" data-id="element-7819">
                      <div className="w-5 h-5 rounded-full bg-[#FFC70A] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-7820">
                        2
                      </div>
                      <span className="text-xs font-medium text-[#B28B07]" data-id="element-7821">
                        {t('components.ShareLinkModal.share')}
                      </span>
                    </div>
                  </div>}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-500 transition-colors" data-id="element-7822">
            <XIcon size={20} aria-hidden="true" data-id="element-7823" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[65vh] overflow-y-auto" data-id="element-7824">
          {step === 'preview' && splitType === 'dates' && renderSchedulePreview()}
          {step === 'preview' && splitType === 'commission' && renderCommissionPreview()}
          {step === 'share' && hasMultipleRecipients && renderMultiRecipientShare()}
          {step === 'share' && !hasMultipleRecipients && renderStandardShare()}
        </div>

        {/* Footer */}
        {renderFooter()}
      </div>
    </div>, document.body);
}